import {
  CampaignStatusesEnum,
  SimulationSchedulerItem,
} from '@/client/campaigns';
import { TargetEntity } from '@/common/types';
import i18n from '@/i18n';
import { DateFormats } from '@/system-settings/enums';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import { EditIcon, TrashIcon } from '@/utils/helpers';
import { t } from 'i18next';
import moment from 'moment';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const GanttContainer = styled.div`
  background: white;
  padding: var(--default-padding);
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
  border-radius: var(--semi-medium-border-radius);
  border: 1px solid var(--gray-moderate);
`;

const GanttHeader = styled.div`
  display: flex;
  position: relative;
  min-width: 800px;
`;

const TimeColumnsContainer = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
`;

const TimelineLabel = styled.div`
  min-width: 220px;
  margin-right: var(--medium-padding);
  font-size: var(--small-font-size);
  font-weight: 600;
  color: var(--black-main);
  display: flex;
  align-items: center;
  padding: var(--small-padding) var(--xsmall-padding);
  border-radius: var(--xxxsmall-border-radius) 0 0 var(--xxxsmall-border-radius);
`;

const TimeColumn = styled.div<{ flexBasis: number }>`
  flex: 0 1 ${(props) => props.flexBasis * 100}%;
  text-align: left;
  font-size: var(--xxsmall-font-size);
  font-weight: 500;
  padding: var(--small-padding) var(--xsmall-padding);
  padding-left: var(--xxxsmall-padding);
  position: relative;
  text-align: left;
  min-width: 0;

  &:last-child {
    border-right: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: 0.5px;
    background: var(--gray-moderate);
  }
`;

const GanttRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  min-width: 800px;
  padding: var(--medium-padding) 0;
  gap: var(--medium-padding);

  &:not(.last-row) {
    border-bottom: 1px solid var(--gray-moderate);
  }

  .bar {
    position: absolute;
    width: 0;
    height: 100%;
    transition: width 0.2s ease;
    border-radius: var(--xsmall-border-radius);
    background: var(--green-chart-pale);
    font-size: var(--small-font-size);
    font-weight: 500;

    > * {
      opacity: 0;
      white-space: nowrap;
    }

    svg {
      width: 16px;
      height: 16px;
      margin-left: var(--semi-xsmall-padding);
    }

    svg path {
      fill: var(--green-color);
    }

    i {
      margin-left: var(--semi-xsmall-padding);
      color: var(--green-color);
    }
  }

  .info-container {
    position: relative;
    max-width: 220px;
    min-height: 70px;

    > :not(.bar) {
      transition: opacity 0.2s ease;
    }

    &:hover:not(.disabled) {
      .bar {
        cursor: pointer;
        width: 100%;

        > * {
          opacity: 1;
        }
      }

      > :not(.bar) {
        opacity: 0;
      }
    }
  }
`;

const TaskLabel = styled.div<{ noPaddingTop?: boolean }>`
  width: 100%;
  min-width: 200px;
  margin-right: 16px;
  font-size: var(--small-font-size);
  font-weight: 500;
  color: var(--black-main);
  padding: var(--xxsmall-padding) 0;
  border-radius: var(--xxxsmall-border-radius);
  line-height: 150%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${(props) => props.noPaddingTop && 'padding-top: 0;'}
`;

const SecondaryLabel = styled.div`
  font-size: var(--xxsmall-font-size);
  font-weight: 500;
  color: var(--gray-darker);
`;

const TaskBarContainer = styled.div`
  flex: 1;
  height: 26px;
  position: relative;
  border-radius: var(--xxsmall-border-radius);
  margin-right: var(--medium-padding);
  background: var(--gray-soft);
`;

const TaskBar = styled.div<{
  disabled?: boolean;
  left: number;
  width: number;
  color?: string;
  isDragging?: boolean;
}>`
  position: absolute;
  top: 0;
  left: ${(props) => props.left}%;
  width: ${(props) => props.width}%;
  height: 26px;
  background: ${(props) => props.color};
  border-radius: var(--xxsmall-border-radius);
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  color: white;
  z-index: ${(props) => (props.isDragging ? 10 : 1)};
  overflow: hidden;

  ${(props) => props.disabled && 'cursor: initial;'}

  .edit-icon {
    opacity: 0;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    display: flex;

    svg {
      width: 16px;
      height: 16px;
    }
    svg path {
      fill: var(--red-dark);
    }
  }

  &:hover .edit-icon {
    opacity: 1;
  }

  &[data-dragging='true'] .edit-icon {
    opacity: 1;
  }
`;

const TaskHandle = styled.div<{ position: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${(props) => (props.position === 'left' ? 'left: 0;' : 'right: 0;')}
  transform: translateY(-50%);
  width: 6px;
  height: 100%;
  background: var(--red-dark);
  cursor: ${(props) => (props.position === 'left' ? 'w-resize' : 'e-resize')};
  opacity: 0;
  z-index: 5;
  transition: all 0.2s ease-in-out;

  ${TaskBar}:hover & {
    opacity: 1;
  }

  ${TaskBar}[data-dragging="true"] & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--xsmall-padding);
  border-radius: var(--xxxsmall-border-radius);
  color: var(--black-main);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  &:hover:not([disabled]) {
    background: #f0f0f0;
    color: #333;
  }

  &[disabled] {
    cursor: initial;
    opacity: 0.5;
  }
`;

const CustomTooltip = styled.div<{
  visible: boolean;
  left: number;
}>`
  position: absolute;
  left: ${(props) => props.left}%;
  top: -32px;
  transform: translateX(-50%);
  background: var(--purple-dark);
  color: var(--white-main);
  padding: 6px 10px;
  border-radius: 4px;
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  pointer-events: none;
  z-index: 1000;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  white-space: nowrap;
  text-align: center;
  min-width: 80px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: var(--purple-dark);
  }
`;

const Legend = styled.div`
  display: flex;
  gap: var(--default-padding);
  padding-top: 16px;
  padding-bottom: 16px;
  justify-content: center;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  font-size: var(--small-font-size);
`;

const LegendDot = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  background: ${(props) => props.color};
  border-radius: 50%;
`;

const DateMissingBar = styled.div`
  flex: 1;
  min-height: 40px;
  align-self: stretch;
  position: relative;
  border-radius: var(--xsmall-border-radius);
  margin-right: var(--medium-padding);
  background: var(--yellow-pale);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--xsmall-padding);
  cursor: pointer;
  font-size: var(--small-font-size);
  font-weight: 500;
  color: var(--yellow-dark);

  &:hover {
    opacity: 0.85;
  }

  i {
    font-size: var(--medium-font-size);
  }
`;

const StyledProgressSpinner = styled(ProgressSpinner)`
  width: 16px;
  height: 16px;
`;

export type SimulationSchedulerProps = {
  simulations: SimulationSchedulerItem[];
  deletingSimulationId?: string | null;
  onSimulationChange?: (simulation: SimulationSchedulerItem) => void;
  onSimulationDelete?: (simulationId: string) => void;
  onSimulationEdit?: (simulation: SimulationSchedulerItem) => void;
  onSimulationSendTestEmail?: (simulation: SimulationSchedulerItem) => void;
  className?: string;
};

export const SimulationScheduler: React.FC<SimulationSchedulerProps> = ({
  simulations,
  deletingSimulationId,
  onSimulationChange,
  onSimulationDelete,
  onSimulationEdit,
  onSimulationSendTestEmail,
  className,
}) => {
  const [draggedSimulation, setDraggedSimulation] = useState<string | null>(
    null,
  );
  const [dragType, setDragType] = useState<
    'move' | 'resize-left' | 'resize-right'
  >('move');
  const [dragOffset, setDragOffset] = useState(0);
  const [tooltipSimulation, setTooltipSimulation] =
    useState<SimulationSchedulerItem | null>(null);
  const [hoveredSimulation, setHoveredSimulation] =
    useState<SimulationSchedulerItem | null>(null);
  const ganttRef = useRef<HTMLDivElement>(null);
  const taskBarContainerRef = useRef<HTMLDivElement | null>(null);

  const formatDate = (date: Date) => {
    return moment(date)
      .locale(i18n.language)
      .format(DateFormats.DATEONLY_SHORT_REVERSE);
  };

  const generateTimeColumns = () => {
    const columns = [];
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const endDate = new Date(
      today.getFullYear() + 1,
      today.getMonth(),
      today.getDate(),
    );

    // Calculate total days in the timeline for proportional widths
    const totalDays = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setMonth(d.getMonth() + 1)
    ) {
      const monthStart = new Date(d);
      const nextMonthStart = new Date(d.getFullYear(), d.getMonth() + 1, 1);

      const monthStartInTimeline = new Date(
        Math.max(monthStart.getTime(), startDate.getTime()),
      );
      const monthEndInTimeline = new Date(
        Math.min(nextMonthStart.getTime(), endDate.getTime()),
      );

      const daysInMonth = Math.ceil(
        (monthEndInTimeline.getTime() - monthStartInTimeline.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      const flexBasis = daysInMonth / totalDays;

      const monthName = moment(monthStart)
        .locale(i18n.language)
        .format(DateFormats.MONTH_SHORT);

      columns.push({
        date: new Date(monthStart),
        label: `${monthName}`,
        flexBasis,
      });
    }

    return columns;
  };

  const timeColumns = generateTimeColumns();

  const getSimulationPosition = useCallback(
    (simulation: SimulationSchedulerItem) => {
      if (!simulation.startDate || !simulation.endDate)
        return { left: 0, width: 0 };
      const today = new Date();
      const timelineStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const timelineEnd = new Date(
        today.getFullYear() + 1,
        today.getMonth(),
        today.getDate(),
      );
      const totalDays = Math.ceil(
        (timelineEnd.getTime() - timelineStart.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      // Calculate the visible area of the campaign in the timeline
      const visibleStart = new Date(
        Math.max(simulation.startDate.getTime(), timelineStart.getTime()),
      );
      const visibleEnd = new Date(
        Math.min(simulation.endDate.getTime(), timelineEnd.getTime()),
      );

      // If the campaign is completely outside the timeline
      if (visibleEnd <= timelineStart || visibleStart >= timelineEnd) {
        return { left: 0, width: 0 };
      }

      const visibleStartDays = Math.ceil(
        (visibleStart.getTime() - timelineStart.getTime()) /
          (1000 * 60 * 60 * 24),
      );
      const visibleDurationDays = Math.ceil(
        (visibleEnd.getTime() - visibleStart.getTime()) / (1000 * 60 * 60 * 24),
      );

      const left = Math.max(0, (visibleStartDays / totalDays) * 100);
      const width = Math.max(1, (visibleDurationDays / totalDays) * 100);

      return { left, width };
    },
    [],
  );

  const getDateFromPosition = useCallback((position: number) => {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const endDate = new Date(
      today.getFullYear() + 1,
      today.getMonth(),
      today.getDate(),
    );

    const totalDays = Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    const daysFromStart = (position / 100) * totalDays;
    const newDate = new Date(
      startDate.getTime() + daysFromStart * (1000 * 60 * 60 * 24),
    );

    return newDate < today ? new Date(today) : newDate;
  }, []);

  const handleMouseDown = (
    e: React.MouseEvent,
    simulationId: string,
    type: 'move' | 'resize-left' | 'resize-right',
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const taskBarElement =
      type === 'move'
        ? (e.currentTarget as HTMLElement)
        : (e.currentTarget as HTMLElement).parentElement;
    const container = taskBarElement?.parentElement as HTMLDivElement | null;
    if (!container) return;
    taskBarContainerRef.current = container;

    const taskBarRect = container.getBoundingClientRect();
    if (!taskBarRect.width) return;

    const mouseX = e.clientX - taskBarRect.left;
    const simulation = simulations.find((s) => s.id === simulationId);
    if (!simulation) return;

    const { left, width } = getSimulationPosition(simulation);
    const simulationLeftPx = (left / 100) * taskBarRect.width;
    const simulationRightPx = ((left + width) / 100) * taskBarRect.width;

    let offset = 0;
    if (type === 'move') {
      offset = mouseX - simulationLeftPx;
    } else if (type === 'resize-left') {
      offset = mouseX - simulationLeftPx;
    } else if (type === 'resize-right') {
      offset = mouseX - simulationRightPx;
    }

    setDragOffset(offset);
    setDraggedSimulation(simulationId);
    setDragType(type);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!draggedSimulation || !taskBarContainerRef.current) return;

      const taskBarRect = taskBarContainerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - taskBarRect.left;
      const adjustedX = mouseX - dragOffset;
      const percentage = Math.max(
        0,
        Math.min(100, (adjustedX / taskBarRect.width) * 100),
      );

      const simulation = simulations.find((s) => s.id === draggedSimulation);
      if (!simulation || !simulation.startDate || !simulation.endDate) return;

      let newStartDate = simulation.startDate;
      let newEndDate = simulation.endDate;

      const today = moment().toDate();
      const minDuration = 1 * 60 * 60 * 1000; // 1 hour minimum

      if (dragType === 'move') {
        const duration =
          simulation.endDate.getTime() - simulation.startDate.getTime();
        const dateFromPosition = getDateFromPosition(percentage);
        const isToday = moment(dateFromPosition).isSame(today, 'day');
        const currentHour = moment(today).hour();
        const hourToUse = isToday && currentHour > 9 ? currentHour + 1 : 9;
        newStartDate = moment(dateFromPosition)
          .hour(hourToUse)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate();
        newEndDate = moment(newStartDate.getTime() + duration)
          .hour(15)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate();

        if (newStartDate < today) {
          newStartDate = new Date(today);
          newEndDate = new Date(today.getTime() + duration);
        }

        const timelineEnd = new Date(
          today.getFullYear() + 1,
          today.getMonth(),
          today.getDate(),
        );
        if (newEndDate > timelineEnd) {
          newEndDate = new Date(timelineEnd);
          newStartDate = new Date(timelineEnd.getTime() - duration);
        }
      } else if (dragType === 'resize-left') {
        const newStartDateRaw = getDateFromPosition(percentage);

        newStartDate = moment(
          new Date(Math.max(today.getTime(), newStartDateRaw.getTime())),
        ).toDate();

        const maxStartDate = new Date(
          simulation.endDate.getTime() - minDuration,
        );
        if (newStartDate > maxStartDate) {
          newStartDate = new Date(maxStartDate);
        }

        const timelineStart = new Date(
          today.getFullYear(),
          today.getMonth(),
          1,
        );
        if (newStartDate < timelineStart) {
          newStartDate = new Date(timelineStart);
        }
        const isToday = moment(newStartDate).isSame(today, 'day');
        const currentHour = moment(today).hour();
        const hourToUse = isToday && currentHour > 9 ? currentHour + 1 : 9;
        newStartDate = moment(newStartDate)
          .hour(hourToUse)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate();
      } else if (dragType === 'resize-right') {
        const newEndDateRaw = getDateFromPosition(percentage);

        newEndDate = moment(
          new Date(
            Math.max(
              simulation.startDate.getTime() + minDuration,
              newEndDateRaw.getTime(),
            ),
          ),
        ).toDate();

        const timelineEnd = new Date(
          today.getFullYear() + 1,
          today.getMonth(),
          31,
        );
        if (newEndDate > timelineEnd) {
          newEndDate = new Date(timelineEnd);
        }

        if (newEndDate < newStartDate) {
          newEndDate = new Date(newStartDate.getTime() + minDuration);
        }

        const isToday = moment(newEndDate).isSame(today, 'day');
        const currentHour = moment(today).hour();
        const hourToUse = isToday && currentHour > 15 ? currentHour + 2 : 15;
        newEndDate = moment(newEndDate)
          .hour(hourToUse)
          .minute(0)
          .second(0)
          .millisecond(0)
          .toDate();
      }

      const updatedSimulation = {
        ...simulation,
        startDate: newStartDate,
        endDate: newEndDate,
      };

      onSimulationChange?.(updatedSimulation);
      setTooltipSimulation(updatedSimulation);
    },
    [
      draggedSimulation,
      dragType,
      dragOffset,
      simulations,
      onSimulationChange,
      getDateFromPosition,
      getSimulationPosition,
    ],
  );

  const handleMouseUp = useCallback(() => {
    setDraggedSimulation(null);
    setTooltipSimulation(null);
  }, []);

  useEffect(() => {
    if (draggedSimulation) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggedSimulation, handleMouseMove, handleMouseUp]);

  return (
    <GanttContainer className={className} ref={ganttRef}>
      <GanttHeader>
        <TimelineLabel></TimelineLabel>
        <TimeColumnsContainer>
          {timeColumns.map((column, index) => (
            <TimeColumn key={index} flexBasis={column.flexBasis}>
              {column.label}
            </TimeColumn>
          ))}
        </TimeColumnsContainer>
        <div style={{ width: 60 }} />
      </GanttHeader>

      {simulations.map((simulation, index) => {
        const isDateInvalid =
          simulation.status === CampaignStatusesEnum.DRAFT &&
          !simulation.startDate;
        const { left, width } = getSimulationPosition(simulation);
        const taskColor = simulation.color;

        return (
          <GanttRow
            key={simulation.id}
            className={index === simulations.length - 1 ? 'last-row' : ''}
          >
            <FlexContainer
              direction="column"
              gap={0}
              width="auto"
              align="flex-start"
              className={`info-container ${
                simulation.disabled &&
                simulation.status !== CampaignStatusesEnum.SCHEDULED
                  ? 'disabled'
                  : ''
              }`}
            >
              {simulation.phishingSign && (
                <AppChip
                  label={t(`campaign.template.${simulation.phishingSign}`)}
                  type="green"
                  size="s"
                />
              )}
              <TaskLabel noPaddingTop={!simulation.phishingSign}>
                {simulation.name}
              </TaskLabel>
              <SecondaryLabel>
                {simulation.targetGroup &&
                Object.values(simulation.targetGroup)?.length ? (
                  Object.values(simulation.targetGroup)
                    .map((enrolled: TargetEntity[]) =>
                      enrolled.map((enrolled: TargetEntity) => enrolled.name),
                    )
                    .flat()
                    .join(', ')
                ) : (
                  <span>{t('phishing.simulations.targetGroup.allUsers')}</span>
                )}{' '}
                ({simulation.targetUsersCount})
              </SecondaryLabel>
              {!simulation.disabled && (
                <FlexContainer
                  className="bar"
                  justify="center"
                  align="center"
                  onClick={() => onSimulationEdit?.(simulation)}
                >
                  <span>{t('phishing.simulations.modal.editCampaign')}</span>{' '}
                  <EditIcon />
                </FlexContainer>
              )}
              {simulation.status === CampaignStatusesEnum.SCHEDULED && (
                <FlexContainer
                  className="bar"
                  justify="center"
                  align="center"
                  onClick={() => onSimulationSendTestEmail?.(simulation)}
                >
                  <span>{t('simulations.testEmail.send')}</span>{' '}
                  <i className="pi pi-envelope" />
                </FlexContainer>
              )}
            </FlexContainer>
            {isDateInvalid ? (
              <DateMissingBar onClick={() => onSimulationEdit?.(simulation)}>
                <span>{t('phishing.simulations.selectDate')}</span>
                <i className="pi pi-calendar" />
              </DateMissingBar>
            ) : (
              <TaskBarContainer>
                <TaskBar
                  left={left}
                  width={width}
                  color={taskColor}
                  isDragging={draggedSimulation === simulation.id}
                  disabled={simulation.disabled}
                  data-dragging={draggedSimulation === simulation.id}
                  data-simulation-id={simulation.id}
                  onMouseDown={(e) =>
                    !simulation.disabled
                      ? handleMouseDown(e, simulation.id, 'move')
                      : null
                  }
                  onMouseEnter={() => setHoveredSimulation(simulation)}
                  onMouseLeave={() => setHoveredSimulation(null)}
                >
                  {!simulation.disabled && (
                    <>
                      <TaskHandle
                        position="left"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleMouseDown(e, simulation.id, 'resize-left');
                        }}
                      />
                      <div
                        className="edit-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSimulationEdit?.(simulation);
                        }}
                      >
                        <EditIcon />
                      </div>
                      <TaskHandle
                        position="right"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleMouseDown(e, simulation.id, 'resize-right');
                        }}
                      />
                    </>
                  )}
                </TaskBar>
                {(tooltipSimulation &&
                  tooltipSimulation.id === simulation.id) ||
                (hoveredSimulation &&
                  hoveredSimulation.id === simulation.id &&
                  !!simulation.startDate &&
                  !!simulation.endDate) ? (
                  <CustomTooltip visible={true} left={left + width / 2}>
                    {`${formatDate(
                      simulation.startDate || new Date(),
                    )} - ${formatDate(simulation.endDate || new Date())}`}
                  </CustomTooltip>
                ) : null}
              </TaskBarContainer>
            )}

            <ActionButton
              disabled={
                !simulation.canDelete || deletingSimulationId === simulation.id
              }
              onClick={() => onSimulationDelete?.(simulation.id)}
            >
              {deletingSimulationId === simulation.id ? (
                <StyledProgressSpinner />
              ) : (
                <TrashIcon />
              )}
            </ActionButton>
          </GanttRow>
        );
      })}

      <Legend>
        <LegendItem>
          <LegendDot color="var(--green-chart-dark)" />
          <span>{t('phishing.simulations.status.currentlyRunning')}</span>
        </LegendItem>
        <LegendItem>
          <LegendDot color="var(--green-color)" />
          <span>{t('phishing.simulations.status.scheduled')}</span>
        </LegendItem>
        <LegendItem>
          <LegendDot color="var(--red-brown)" />
          <span>{t('phishing.simulations.status.notScheduled')}</span>
        </LegendItem>
        {simulations.some(
          (simulation) =>
            simulation.status === CampaignStatusesEnum.FAILED_LAUNCH,
        ) && (
          <LegendItem>
            <LegendDot color="var(--red-main)" />
            <span>{t('phishing.simulations.status.failedToLaunch')}</span>
          </LegendItem>
        )}
        {simulations.some(
          (simulation) =>
            simulation.status === CampaignStatusesEnum.DRAFT &&
            !simulation.startDate,
        ) && (
          <LegendItem>
            <LegendDot color="var(--yellow-pale)" />
            <span>{t('phishing.simulations.status.dateMissing')}</span>
          </LegendItem>
        )}
      </Legend>
    </GanttContainer>
  );
};
